<template>
    <div>
    <form-modal
        ref="formModal"
        :title="i18nJustification">
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <confirmation-form-modal
                    ref="confirmationFormModal"
                    :title="i18nJustification"
                    :operation="'deleted'"
                />
                <confirmation-form-modal
                    ref="rejectConfirmationModal"
                    :title="i18nJustification"
                    :operation="'edited'"
                    :description="i18nRejectConfirmation"
                    :message="i18nIrreversibleWarning"
                />
                <b-form-row>
                    <b-col md="12">
                        <b-form-group
                            :label="getI18n('USERS', 'TITLES.user')"
                            class="label-pdv mb-0">
                            <div class="text-pdv">
                                <p v-if="justificationModal.user">
                                    {{ justificationModal.user.name }}
                                </p>
                                <p v-else>
                                    {{ getI18n('COMMON', 'uninformed') }}
                                </p>
                            </div>
                        </b-form-group>
                        <b-form-group
                            :label="getI18n('BUSINESS_UNITS', 'TITLES.business_unit')"
                            class="label-pdv mb-0">
                            <div class="text-pdv">
                                <p v-if="justificationModal.businessUnit">
                                    {{ justificationModal.businessUnit.name }}
                                </p>
                                <p v-else>
                                    {{ getI18n('COMMON', 'uninformed') }}
                                </p>
                            </div>
                        </b-form-group>
                        <b-form-group
                            :label="getI18n('POS', 'TITLES.pos')"
                            class="label-pdv mb-0">
                            <div class="text-pdv">
                                <p v-if="justificationModal.pointOfSale">
                                    {{ justificationModal.pointOfSale.name }}
                                </p>
                                <p v-else>
                                    {{ getI18n('COMMON', 'uninformed') }}
                                </p>
                            </div>
                        </b-form-group>
                        <b-form-group
                            :label="getI18n('JUSTIFICATION', 'TITLES.justification_type')"
                            class="label-pdv mb-0">
                            <div class="text-pdv">
                                <p v-if="justificationModal.justificationType">
                                    {{ justificationModal.justificationType.name }}
                                </p>
                                <p v-else>
                                    {{ getI18n('COMMON', 'uninformed') }}
                                </p>
                            </div>
                        </b-form-group>
                        <b-form-group
                            :label="getI18n('JUSTIFICATION', 'TITLES.justification')"
                            class="label-pdv mb-0">
                            <div class="text-pdv">
                                <p v-if="justificationModal.justification">
                                    {{ justificationModal.justification }}
                                </p>
                                <p v-else>
                                    {{ getI18n('COMMON', 'uninformed') }}
                                </p>
                            </div>
                        </b-form-group>
                        <b-form-group
                            v-if="showRejectForm"
                            :label="i18nRejectionReason"
                            class="required label-pdv"
                            label-for="rejection-reason"
                            :invalid-feedback="i18nInvalidReasonLength"
                        >
                            <b-form-textarea
                                id="rejection-reason"
                                v-model.trim="$v.rejectForm.reason.$model"
                                class="input-pdv-blue"
                                rows="6"
                                max-rows="6"
                                autofocus
                                autocomplete="off"
                                :disabled="submitLoading"
                                :state="validateField('reason', 'rejectForm')"
                                @input="clearResponseError('reason')"
                                @blur="$v.rejectForm.reason.$touch"
                            />
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </form>
        </template>
        <template v-if="!readOnly" v-slot:footer>
            <div v-if="showRejectForm" class="d-flex justify-content-between">
                <save-button
                    child-class="col-sm-5 float-left"
                    :busy="submitLoading"
                    :disabled="submitLoading"
                    :title="i18nReprove"
                    @onClick="handleConfirmReject"
                />
                <cancel-label-button
                    :disabled="submitLoading"
                    @onClick="cancelRejectForm"
                />
            </div>
            <div v-else class="d-flex justify-content-between">
                <save-button
                    v-if="isPending"
                    ref="okButton"
                    child-class="col-sm-4 float-left"
                    :busy="submitLoading"
                    :disabled="deleteLoading || isApproved || isRejected"
                    :title="i18nApprove"
                    @onClick="handleOk"
                />
                <reject-label-button
                    v-if="isPending"
                    class="col-sm-4"
                    :busy="submitLoading"
                    :disabled="deleteLoading || submitLoading"
                    :title="i18nReprove"
                    @onClick="startRejectForm"
                />
                <save-button
                    v-if="isRejected"
                    child-class="col-sm-5 float-left"
                    :title="i18nViewRejectionReason"
                    @onClick="showRejectionReasonModal"
                />
                <delete-button
                    ref="deleteButton"
                    child-class="col-sm-4 float-right"
                    :busy="deleteLoading"
                    :disabled="submitLoading"
                    @onClick="handleDelete"
                />
            </div>
        </template>
    </form-modal>
    <form-modal
        ref="rejectionReasonModal"
        :title="i18nRejectionReason">
        <template v-slot:form>
            <b-form-group
                :label="i18nRejectionReason"
                class="label-pdv"
                label-for="view-rejection-reason"
            >
                <b-form-textarea
                    id="view-rejection-reason"
                    :value="justificationModal.rejection_reason"
                    class="input-pdv-blue"
                    rows="6"
                    max-rows="6"
                    disabled
                />
            </b-form-group>
        </template>
        <template v-slot:footer>
            <save-button
                :title="i18nClose"
                @onClick="hideRejectionReasonModal"
            />
        </template>
    </form-modal>
    </div>
</template>

<script>
import { requiredIf, minLength } from 'vuelidate/lib/validators'
import ConfirmationFormModal from '@src/components/ConfirmationFormModal'
import swalFeedback from '@src/mixins/swalFeedback.vue'
import validationMixin from '@src/mixins/validation.vue'
import FormModal from '@/src/components/FormModal.vue'
import fieldsValidation from '@src/mixins/fieldsValidation.vue'
import DeleteButton from '@/src/components/DeleteButton'
import SaveButton from '@/src/components/SaveButton'
import RejectLabelButton from '@/src/components/RejectLabelButton.vue'
import CancelLabelButton from '@/src/components/CancelLabelButton'

import JustificationService from '@src/services/JustificationService'

const i18nKey = 'JUSTIFICATION'
const i18nCommon = 'COMMON'

export default {
    components: {
        ConfirmationFormModal,
        FormModal,
        DeleteButton,
        SaveButton,
        RejectLabelButton,
        CancelLabelButton,
    },
    mixins: [validationMixin, swalFeedback, fieldsValidation],
    props: {
        modal: {
            type: Object,
            default: () => {
                return {
                    id: 0,
                    business_unit_id: 0,
                    businessUnit: {
                        id: 0,
                        name: '',
                    },
                    justification_type_id: 0,
                    justificationType: {
                        id: 0,
                        name: '',
                    },
                    work_schedule_id: 0,
                    pointOfSale: {
                        id: 0,
                        name: ''
                    },
                    user_id: 0,
                    user: {
                        id: 0,
                        name: ''
                    },
                    justification: '',
                    justification_date: null,
                    status: false,
                    rejection_reason: '',
                    rejected_at: null,
                }
            },
        },
    },
    data() {
        return {
            i18nKey: i18nKey,
            i18nCommon: i18nCommon,
            justificationModal: this.modal,
            readOnly: false,
            submitLoading: false,
            deleteLoading: false,
            showRejectForm: false,
            rejectForm: {
                reason: '',
            },
        }
    },
    validations: {
        justificationModal: {},
        rejectForm: {
            reason: {
                required: requiredIf(function () {
                    return this.showRejectForm
                }),
                minLength: minLength(15),
            },
        },
    },
    computed: {
        i18nName() {
            return this.getI18n(i18nCommon, 'name')
        },
        i18nFlagInvalidNameLabel() {
            return this.getI18n('ERROR_CODES.invalid_name')
        },
        i18nApprove() {
            return this.getI18n(i18nCommon, 'approve')
        },
        i18nReprove() {
            return this.getI18n(i18nCommon, 'reprove')
        },
        i18nViewRejectionReason() {
            return this.getI18n(i18nKey, 'BUTTONS.view_rejection_reason')
        },
        i18nRejectionReason() {
            return this.getI18n(i18nKey, 'FIELDS.rejection_reason')
        },
        i18nInvalidReasonLength() {
            return this.getI18n('ERROR_CODES', 'invalid_reason_length')
        },
        i18nRejectConfirmation() {
            return this.getI18n('RESPONSES', 'reject_confirmation_text')
        },
        i18nIrreversibleWarning() {
            return this.getI18n('RESPONSES', 'warning_text')
        },
        i18nClose() {
            return this.getI18n(i18nCommon, 'close')
        },
        i18nNewPosFlag() {
            return this.getI18n(i18nKey, 'BUTTONS.new_flag')
        },
        i18nEditPosFlag() {
            return `${this.getI18n(i18nCommon, 'edit')} ${this.getI18n(
                i18nKey,
                'TITLES.justification'
            )}`
        },
        i18nJustification() {
            return this.getI18nModified({
                prefix: i18nKey + '.TITLES.justification',
                modifier: 1,
            })
        },
        isRejected() {
            return Boolean(this.justificationModal.rejected_at)
        },
        isApproved() {
            return Boolean(this.justificationModal.status) && !this.isRejected
        },
        isPending() {
            return !this.isApproved && !this.isRejected
        },
    },
    methods: {
        showModal(readOnly) {
            this.readOnly = readOnly
            this.justificationModal = this.modal
            this.submitLoading = false
            this.deleteLoading = false
            this.showRejectForm = false
            this.rejectForm.reason = ''
            this.$refs.formModal.show()
            this.resetValidation()
        },
        clearAllResponseError() {
            this.modalResponseErrors = {}
        },
        cleanModal() {
            this.justificationModal = Object.assign(this.justificationModal, {
                id: 0,
                business_unit_id: 0,
                businessUnit: {
                    id: 0,
                    name: '',
                },
                justification_type_id: 0,
                justificationType: {
                    id: 0,
                    name: '',
                },
                work_schedule_id: 0,
                pointOfSale: {
                    id: 0,
                    name: ''
                },
                user_id: 0,
                user: {
                    id: 0,
                    name: ''
                },
                justification: '',
                justification_date: null,
                status: false,
                rejection_reason: '',
                rejected_at: null,
            })
            this.showRejectForm = false
            this.rejectForm.reason = ''

            this.$v.justificationModal.$reset()
        },
        handleCancel() {
            this.resetValidation()
            this.$refs.formModal.hide()
        },
        resetValidation() {
            this.$nextTick(() => this.$v.$reset())
            this.clearAllResponseError()
        },
        startRejectForm() {
            this.showRejectForm = true
            this.rejectForm.reason = ''
            this.resetValidation()
        },
        cancelRejectForm() {
            this.showRejectForm = false
            this.rejectForm.reason = ''
            this.resetValidation()
        },
        handleConfirmReject() {
            this.$v.rejectForm.reason.$touch()
            if (!this.$v.rejectForm.$invalid && !this.submitLoading) {
                this.$refs.rejectConfirmationModal.showModal(false, this.submitReject)
            }
        },
        showRejectionReasonModal() {
            this.$refs.rejectionReasonModal.show()
        },
        hideRejectionReasonModal() {
            this.$refs.rejectionReasonModal.hide()
        },
        handleOk() {
            if (this.isRejected || this.isApproved) {
                return
            }
            this.$v.justificationModal.$touch()
            if (!this.$v.justificationModal.$invalid && !this.submitLoading) {
                this.submitLoading = true
                this.handleSubmit()
            }
        },
        async handleSubmit() {
            try {
                await JustificationService
                    .update(this.justificationModal.id, {'status': true})
                        .then((response) => {
                            const statusCode = response.status.toString()

                            if (statusCode.charAt(0) === '2') {
                                this.$emit('refresh')
                                this.resetValidation()
                                this.$refs.formModal.hide()
                                this.positiveFeedback(this.i18nJustification, 'edited', 3)
                            }
                            else {
                                this.negativeFeedback(this.i18nJustification, response.message)
                            }
                        })
                        .catch((error) => {
                            this.negativeFeedback(this.i18nJustification, error.message)
                        })
            }
            finally {
                this.submitLoading = false
            }
        },
        async submitReject(confirmed) {
            if (!confirmed) {
                return
            }

            try {
                this.submitLoading = true
                await JustificationService
                    .update(this.justificationModal.id, {
                        rejection_reason: this.rejectForm.reason,
                    })
                    .then((response) => {
                        const statusCode = response.status.toString()

                        if (statusCode.charAt(0) === '2') {
                            const data = response.data && response.data.data
                                ? response.data.data
                                : response.data
                            this.justificationModal = Object.assign(
                                {},
                                this.justificationModal,
                                data || {
                                    rejected_at: true,
                                    rejection_reason: this.rejectForm.reason,
                                    status: false,
                                }
                            )
                            this.showRejectForm = false
                            this.$emit('refresh')
                            this.resetValidation()
                            this.positiveFeedback(this.i18nJustification, 'edited', 3)
                        }
                        else {
                            this.negativeFeedback(this.i18nJustification, response.message)
                        }
                    })
                    .catch((error) => {
                        this.negativeFeedback(this.i18nJustification, error.message)
                    })
            }
            finally {
                this.submitLoading = false
            }
        },
        handleDelete() {
            if (this.deleteLoading) {
                return
            }

            this.$refs.confirmationFormModal.showModal(false, this.deleteJustification)
        },
        async deleteJustification(deleteRecord) {
            if (!deleteRecord) {
                return
            }

            try {
                this.$v.justificationModal.$touch()
                this.deleteLoading = true

                await JustificationService.delete(this.justificationModal.id)
                    .then((response) => {
                        const statusCode = response.status.toString()

                        if (statusCode.charAt(0) === '2') {
                            this.$emit('refresh')
                            this.resetValidation()
                            this.$refs.formModal.hide()
                            this.positiveFeedback(this.i18nJustification, 'deleted', 3)
                        }
                        else {
                            this.negativeFeedback(this.i18nJustification, response.message)
                        }
                    })
                    .catch((error) => {
                        this.negativeFeedback(this.i18nJustification, error.message)
                    });
            }
            finally {
                this.deleteLoading = false
            }
        },
    },
}
</script>
